import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Instagram, Facebook, Mail, ChevronRight } from 'lucide-react';
import { foodTruckInfo, schedule } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Menu', path: '/menu' },
    { name: 'Emplacements', path: '/emplacements' },
    { name: 'Avis clients', path: '/avis' },
    { name: 'À propos', path: '/a-propos' },
    { name: 'Contact', path: '/contact' },
  ];

  const openDays = schedule.filter(day => day.open);

  return (
    <footer className="bg-[#0E0E0E] border-t border-white/10">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-[#F7C948] rounded-xl flex items-center justify-center">
                <span className="font-display text-2xl font-bold text-[#0E0E0E]">JD</span>
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-white">Just'Une Dalle</h3>
                <p className="text-sm text-gray-400">Food Truck</p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              L'accueil qui fait autant plaisir que la commande. Burgers, tacos et street-food gourmande à Malafretaz.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={foodTruckInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:bg-[#F7C948] hover:text-[#0E0E0E] transition-all duration-300"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
              <a
                href={foodTruckInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:bg-[#F7C948] hover:text-[#0E0E0E] transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-lg font-bold text-white mb-6">Navigation</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="flex items-center gap-2 text-gray-400 hover:text-[#F7C948] transition-colors duration-300 group"
                  >
                    <ChevronRight size={16} className="text-[#F7C948] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Horaires */}
          <div>
            <h4 className="font-display text-lg font-bold text-white mb-6 flex items-center gap-2">
              <Clock size={20} className="text-[#F7C948]" />
              Horaires
            </h4>
            <ul className="space-y-2">
              {schedule.map((item) => (
                <li
                  key={item.day}
                  className={`flex justify-between text-sm ${
                    item.open ? 'text-gray-300' : 'text-gray-500'
                  }`}
                >
                  <span>{item.day}</span>
                  <span className={item.open ? 'text-[#F7C948]' : ''}>{item.hours}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-lg font-bold text-white mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-[#F7C948] transition-colors duration-300"
                >
                  <Phone size={20} className="text-[#F7C948] flex-shrink-0 mt-0.5" />
                  <span>{foodTruckInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href="https://maps.google.com/?q=429+Route+de+Bourg+01340+Malafretaz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-gray-400 hover:text-[#F7C948] transition-colors duration-300"
                >
                  <MapPin size={20} className="text-[#F7C948] flex-shrink-0 mt-0.5" />
                  <span>{foodTruckInfo.address}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${foodTruckInfo.email}`}
                  className="flex items-start gap-3 text-gray-400 hover:text-[#F7C948] transition-colors duration-300"
                >
                  <Mail size={20} className="text-[#F7C948] flex-shrink-0 mt-0.5" />
                  <span>{foodTruckInfo.email}</span>
                </a>
              </li>
            </ul>
            <a
              href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 mt-6 bg-[#F7C948] text-[#0E0E0E] px-6 py-3 rounded-xl font-semibold hover:bg-[#FFE17D] transition-all duration-300"
            >
              <Phone size={18} />
              Appeler
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} Just'Une Dalle. Tous droits réservés.
            </p>
            <p className="text-gray-500 text-sm">
              Food Truck à Malafretaz (01340)
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
